import { DateTime, TimeZone } from 'timezonecomplete';

import type { ISchedule, ISortedGroupedSchedule } from '@/types/schedule';
import type { selectUserTimezone } from '@/store/selectors/authSelectors';
import type { selectSchedulesList } from '@/store/selectors/scheduleSelectors';

import { AllShiftItemType } from '@/store/enums';
import { BLOCK_DURATION } from '@/config/variables';
import { groupSchedules } from '@/helpers/schedules';
import { CachedBlock } from '@/classes/cachedBlock';

const combineSchedulesListToGrouppedSchedulesList = (
  schedulesList: ReturnType<typeof selectSchedulesList>,
  userTimezone: ReturnType<typeof selectUserTimezone>,
): ISortedGroupedSchedule[] => {
  if (schedulesList.length === 0 || !userTimezone) return [];

  const mySchedules: ISortedGroupedSchedule[] = schedulesList.flatMap((contract: ISchedule) => {
    // Create new array of a read only contract.shifts
    const scheduleShiftToGroup = [...contract.shifts];

    // group schedules will group shifts that occur one after another
    const grouppedSchedules = groupSchedules(scheduleShiftToGroup, userTimezone);

    return grouppedSchedules.map(schedule => {
      const blockData = CachedBlock.fromBlockId(schedule[0].startBlock, userTimezone);

      return {
        ...contract,
        shifts: schedule.map(shift => ({ ...shift, isFocused: false })),
        dayNumber: blockData.day,
        monthNumber: blockData.month,
        itemData: {
          startBlock: schedule[0].startBlock,
          endBlock: schedule[schedule.length - 1].endBlock,
          itemType: AllShiftItemType.schedule,
          id: `schedule-${contract.scheduleId}-${schedule[0].startBlock}-${schedule[schedule.length - 1].endBlock}`,
        },
      };
    });
  });

  return mySchedules.flatMap(schedule => {
    const startBlock = CachedBlock.fromBlockId(schedule.itemData.startBlock, userTimezone);
    const endBlock = CachedBlock.fromBlockId(schedule.itemData.endBlock, userTimezone);
    const isSplitDay = startBlock.day !== endBlock.day;

    if (!isSplitDay) {
      return schedule;
    }

    const prevItemStartDate = new DateTime(startBlock.utcMillis, TimeZone.utc()).toZone(TimeZone.zone(userTimezone));
    const nextItemEndDate = new DateTime(endBlock.utcMillis, TimeZone.utc()).toZone(TimeZone.zone(userTimezone));

    const midnightToday = nextItemEndDate.startOfDay();
    const prevItemBlockOffset = midnightToday.diff(prevItemStartDate).minutes() / BLOCK_DURATION - 1;
    const nextItemBlockOffset = midnightToday.diff(nextItemEndDate).minutes() / BLOCK_DURATION;

    const PREV_ITEM_LIMIT = startBlock.blockId + prevItemBlockOffset;
    const NEXT_ITEM_LIMIT = endBlock.blockId + nextItemBlockOffset;

    const prevItem: ISortedGroupedSchedule = {
      ...schedule,
      shifts: schedule.shifts.filter(shift => shift.startBlock <= PREV_ITEM_LIMIT),
      splitStart: true,
      dayNumber: startBlock.day,
      monthNumber: startBlock.month,
      originalStartBlock: startBlock.blockId,
      originalEndBlock: endBlock.blockId,
      itemData: {
        ...schedule.itemData,
        id: schedule.itemData.id + '_splitStart',
        endBlock: PREV_ITEM_LIMIT,
      },
    };
    const nextItem: ISortedGroupedSchedule = {
      ...schedule,
      shifts: schedule.shifts.filter(shift => shift.endBlock >= NEXT_ITEM_LIMIT),
      splitEnd: true,
      dayNumber: endBlock.day,
      monthNumber: endBlock.month,
      originalStartBlock: startBlock.blockId,
      originalEndBlock: endBlock.blockId,
      itemData: {
        ...schedule.itemData,
        id: schedule.itemData.id + '_splitEnd',
        startBlock: NEXT_ITEM_LIMIT,
      },
    };
    return [prevItem, nextItem];
  });
};

export { combineSchedulesListToGrouppedSchedulesList };
